import {
  CalendarEvent,
  CalendarEvents,
  Id,
  Schedule,
  Shift,
  ShiftsGroupEvent,
  ShowEventsOptions,
  Store,
  UserRole,
  isShiftEvent,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { isEqual } from 'lodash'
import moment from 'moment'
import { SettingValue } from 'settings/types'
import { storeHasManagementRole } from 'stores/utils'
import {
  defaultIncentivesEnum,
  defaultStartTimeListEnum,
} from './filters/context'
import { Filters, SingleSelectFilter } from './filters/types'

export function getAvailableSchedules(
  stores: Store[] | undefined,
  hasShifts: boolean,
  showCustomFilter?: boolean
): Schedule[] {
  if (!stores) {
    return []
  }

  const schedules: Schedule[] = []

  if (stores.length > 0) {
    if (hasShifts) {
      schedules.push({
        calendar: 'helper',
      })
      schedules.push({
        calendar: 'helper',
        openShiftsOnly: true,
      })
    }

    schedules.push(
      ...stores.flatMap(store => {
        const storeSchedules: Schedule[] = [
          {
            calendar: 'store',
            storeId: store.id,
            openShiftsOnly: false,
          } as const,
        ]

        if (storeHasManagementRole(store)) {
          storeSchedules.push({
            calendar: 'store',
            storeId: store.id,
            openShiftsOnly: true,
          } as const)
          storeSchedules.push({
            calendar: 'store',
            storeId: store.id,
            openShiftsOnly: false,
            shiftsWithAlertsOnly: true,
          } as const)

          if (showCustomFilter) {
            storeSchedules.push({
              calendar: 'store',
              storeId: store.id,
              custom: true,
            } as const)
          }
        }

        return storeSchedules
      })
    )
  } else {
    schedules.push({
      calendar: 'helper',
    })

    schedules.push({
      calendar: 'helper',
      openShiftsOnly: true,
    })
    if (showCustomFilter) {
      schedules.push({ calendar: 'helper', openShiftsOnly: true, custom: true })
    }
  }

  return schedules
}

export function getScheduleTitle(
  schedule: Schedule,
  includeStoreNumber: boolean,
  stores: Store[]
) {
  return getScheduleName(schedule, includeStoreNumber, stores, schedule.custom)
}

export function getScheduleName(
  schedule: Schedule,
  includeStoreNumber: boolean,
  stores: Store[],
  isAppliedFilter?: boolean
) {
  if (isAppliedFilter) {
    let name
    if (schedule.calendar === 'helper') {
      name = t('myOpenShifts')
    } else if (schedule.calendar === 'store') {
      name = t('storeSchedule')
      if (includeStoreNumber) {
        name = includeStoreNumberString(name)
      }
    }
    if (name) {
      return `${name} - ${t('filtered')}`
    }
    return t('schedule')
  }
  if (schedule.calendar === 'helper') {
    if (schedule.openShiftsOnly) {
      if (schedule.custom) {
        return t('customFilter')
      }
      return t('myOpenShifts')
    }
    return t('helperSchedule')
  }

  const scheduleName = schedule.openShiftsOnly
    ? t('openShifts')
    : schedule.shiftsWithAlertsOnly
    ? t('shiftsWithAlerts')
    : schedule.custom
    ? t('customFilter')
    : t('storeSchedule')

  if (includeStoreNumber) {
    return includeStoreNumberString(scheduleName)
  }
  return scheduleName

  function includeStoreNumberString(scheduleNameString: string) {
    const store = stores.find(
      s => schedule.calendar === 'store' && s.id === schedule.storeId
    )
    if (store) {
      return `${scheduleNameString} - ${store.storeNumber}`
    }
    return scheduleNameString
  }
}

export function filterShiftEvents(
  events: CalendarEvent[] | readonly CalendarEvent[] | undefined
): ShiftsGroupEvent[] {
  return (events?.filter(isShiftEvent) as ShiftsGroupEvent[]) ?? []
}

export function filterEvents(
  events: CalendarEvents | undefined,
  schedule: Schedule | undefined,
  filters?: Filters,
  showEvents?: ShowEventsOptions
): CalendarEvents | undefined {
  if (!events) {
    return undefined
  }

  if (schedule) {
    if (
      schedule.openShiftsOnly ||
      schedule.custom ||
      (schedule.calendar === 'store' && schedule.shiftsWithAlertsOnly) ||
      showEvents
    ) {
      return Object.keys(events).reduce(
        (filteredEvents: CalendarEvents, date) => {
          filteredEvents[date] = events[date].flatMap(event => {
            if (
              !isShiftEvent(event) ||
              (schedule.calendar === 'helper' &&
                schedule.openShiftsOnly &&
                !schedule.custom)
            ) {
              if (!showEvents?.holidays && event.type === 'holiday') {
                return []
              }
              if (!showEvents?.timeOff && event.type === 'time_off') {
                return []
              }
              if (!showEvents?.customEvents && event.type === 'custom_events') {
                return []
              }
              if (!showEvents?.tasks && event.type === 'tasks') {
                return []
              }
              if (event.type === 'time_off') {
                //Filtering out time off events that are not approved for store schedules
                if (schedule.calendar === 'store') {
                  if (
                    !event.timeOffEvents.some(
                      timeOffEvent => timeOffEvent.status === 'APPROVED'
                    )
                  ) {
                    return []
                  }
                }
              }

              return event
            }
            const shifts = event.shifts.filter(shift => {
              if (schedule.openShiftsOnly && schedule.calendar === 'store') {
                return shift.open
              } else if (
                schedule.calendar === 'store' &&
                schedule.shiftsWithAlertsOnly
              ) {
                return !!shift.note || !!shift.critical
              } else if (schedule.custom) {
                if (!filters) {
                  return
                }

                for (const [name, filter] of Object.entries(filters)) {
                  switch (filter.type) {
                    case 'boolean': {
                      if (filter.on) {
                        switch (filter.name) {
                          case 'shiftsWithAlerts': {
                            if (!shift.note && !shift.critical) {
                              return false
                            }
                            break
                          }
                          case 'criticalShifts': {
                            if (!shift.critical) {
                              return false
                            }
                            break
                          }
                          case 'shiftsWithMinor': {
                            if (!shift.user?.minor) {
                              return false
                            }
                            break
                          }
                          case 'shiftsWithInterest': {
                            if (!shift.interests || shift.interests === 0) {
                              return false
                            }
                            break
                          }
                          case 'nextShifts': {
                            if (!shift.open) {
                              return false
                            }
                            break
                          }
                          case 'shiftsWithBonus': {
                            if (!shift.bonus) {
                              return false
                            }
                            break
                          }
                        }
                      }
                      break
                    }
                    case 'multiSelect': {
                      if (filter.selectedIds.length !== 0) {
                        if (filter.name === 'crewMembers') {
                          if (
                            !shift.user ||
                            (shift.user?.id &&
                              !filter.selectedIds.includes(shift.user?.id))
                          ) {
                            return false
                          }
                        } else if (filter.name === 'stores') {
                          if (
                            !filter.selectedIds.find(
                              selectedId => shift.store.id === selectedId
                            )
                          ) {
                            return false
                          }
                        } else {
                          if (filter.values.length === 0) {
                            break
                          }
                          const shiftPositionId = filter.values.find(
                            (value: SettingValue) =>
                              value.name === shift.position
                          )?.id
                          if (
                            !shiftPositionId ||
                            (shiftPositionId &&
                              !filter.selectedIds.includes(shiftPositionId))
                          ) {
                            return false
                          }
                        }
                      }
                      break
                    }
                    case 'singleSelect': {
                      if (filter.name === 'startTime') {
                        if (!filterShiftByStartTime(shift, filter)) {
                          return false
                        }
                      } else if (filter.name === 'incentives') {
                        if (!filterShiftByIncentives(shift, filter)) {
                          return false
                        }
                      }
                    }
                  }
                }
                return true
              } else {
                return true
              }
            })

            if (shifts.length === 0) {
              return []
            }

            return { ...event, shifts }
          })
          return filteredEvents
        },
        {}
      )
    }
  }

  return events
}

export function getDefaultSchedule(
  role: UserRole,
  schedules: Schedule[],
  activeStoreId: Id | undefined
) {
  if (schedules.length === 0) {
    return
  }

  if (role === 'helper') {
    const helperSchedule = schedules.find(
      schedule => schedule.calendar === 'helper'
    )

    if (helperSchedule) {
      return helperSchedule
    }
  }

  if (activeStoreId) {
    const storeSchedule = schedules.find(
      schedule =>
        schedule.calendar === 'store' && schedule.storeId === activeStoreId
    )

    if (storeSchedule) {
      return storeSchedule
    }
  }

  return schedules[0]
}

export function isSameSchedule(schedule1: Schedule, schedule2: Schedule) {
  return isEqual(schedule1, schedule2)
}

export function getSimilarSchedule(
  availableSchedules: Schedule[],
  schedule: Schedule,
  storeId: Id
) {
  let similarSchedule: Schedule

  switch (schedule.calendar) {
    case 'helper':
      similarSchedule = { ...schedule }
      break
    case 'store':
      similarSchedule = { ...schedule, storeId }
      break
  }

  if (availableSchedules.find(s => isSameSchedule(s, similarSchedule))) {
    return similarSchedule
  }
}

function filterShiftByIncentives(shift: Shift, filter: SingleSelectFilter) {
  const incentive = shift.amount
  const selectedId = Number(filter.selectedId)
  if (incentive) {
    switch (selectedId) {
      case defaultIncentivesEnum.allNextShifts:
        return true
      case defaultIncentivesEnum.nextShiftsWithIncentives:
        return incentive > 0
      case defaultIncentivesEnum.nextShiftsWithIncentivesMoreThan10Dollars:
        return incentive >= 10
      case defaultIncentivesEnum.nextShiftsWithIncentivesMoreThan20Dollars:
        return incentive >= 20
      case defaultIncentivesEnum.nextShiftsWithoutIncentives:
        return incentive <= 0
    }
  } else if (
    selectedId === defaultIncentivesEnum.nextShiftsWithoutIncentives ||
    selectedId === defaultIncentivesEnum.allNextShifts
  ) {
    return true
  }
  return false
}

function filterShiftByStartTime(shift: Shift, filter: SingleSelectFilter) {
  const startTime = moment(shift.startTime, 'hh:mm').format('x')
  switch (Number(filter.selectedId)) {
    case defaultStartTimeListEnum.breakfast:
      return (
        startTime >= moment('07:00', 'hh:mm').format('x') &&
        startTime <= moment('09:00', 'hh:mm').format('x')
      )
    case defaultStartTimeListEnum.lunch:
      return (
        startTime >= moment('11:00', 'hh:mm').format('x') &&
        startTime <= moment('14:00', 'hh:mm').format('x')
      )
    case defaultStartTimeListEnum.dinner:
      return (
        startTime >= moment('17:00', 'hh:mm').format('x') &&
        startTime <= moment('19:00', 'hh:mm').format('x')
      )
  }
  return true
}
